import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import AppFooter from "./Homepage/AppFooter";
import { useAppContext } from "../context/AppContext";
import {
  motion,
  AnimatePresence,
  useTransform,
  useScroll,
} from "framer-motion";
import {
  ArrowUpRight,
  Briefcase,
  CalendarClock,
  PartyPopper,
  Stethoscope,
  SprayCan as Spray,
  GraduationCap,
  Dumbbell,
  Shield,
  Trophy,
  ChevronLeft,
  ChevronDown,
  User,
  LogOut,
  Calendar,
} from "lucide-react";
import ServicesByCategory from "../pages/ServicesByCategory"; // Adjust path if needed

// --- DATA (with icons from lucide-react) ---
const features = [
  {
    icon: <Briefcase size={24} />,
    title: "My Communities",
    subtitle: "Manage your empire",
    description: "Connect with your local community and manage organization events effortlessly.",
    route: "/app/organization",
  },
  {
    icon: <CalendarClock size={24} />,
    title: "My Appointments",
    subtitle: "Track your timeline",
    description: "Never miss a beat with our intelligent appointment scheduling and tracking.",
    route: "/app/appointments",
  },
  {
    icon: <PartyPopper size={24} />,
    title: "Personal Events",
    subtitle: "Create moments",
    description: "Plan weddings, parties, and gatherings with comprehensive tools.",
    route: "/app/events/new",
  },
];




const focusList = [
  {
    title: "Review Community Updates",
    description: "New requests waiting for approval.",
  },
  {
    title: "Confirm Tomorrow’s Appointments",
    description: "Send reminders with one tap.",
  },
  {
    title: "Plan Your Next Event",
    description: "Draft the agenda and invite list.",
  },
];

const serviceCategories = [
  { 
    label: "Doctor", 
    icon: <Stethoscope size={32} />, 
    image: "https://images.unsplash.com/photo-1666214280557-f1b5022eb634?auto=format&fit=crop&q=80" 
  },
  { 
    label: "Cleaner", 
    icon: <Spray size={32} />, 
    image: "https://images.unsplash.com/photo-1581578949510-fa7315c4c350?q=80&w=687&auto=format&fit=crop&q=80" 
  },
  { 
    label: "Education", 
    icon: <GraduationCap size={32} />, 
    image: "https://images.unsplash.com/photo-1524178232363-1fb2b075b655?q=80&w=1170&auto=format&fit=crop&q=80" 
  },
  { 
    label: "Fitness", 
    icon: <Dumbbell size={32} />, 
    image: "https://images.unsplash.com/photo-1534438327276-14e5300c3a48?auto=format&fit=crop&q=80" 
  },
  { 
    label: "Security", 
    icon: <Shield size={32} />, 
    image: "https://images.unsplash.com/photo-1555963966-b7ae5404b6ed?auto=format&fit=crop&q=80" 
  },
  { 
    label: "Sports", 
    icon: <Trophy size={32} />, 
    image: "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?auto=format&fit=crop&q=80" 
  },
];

const DashboardHeader = ({ opacity }) => {
  const { handleLogout } = useAppContext();
  const [menuOpen, setMenuOpen] = useState(false);
  const navigate = useNavigate();

  const goToCalendar = () => {
    navigate("/app/calendar");
    setMenuOpen(false);
  };

  const onLogout = () => {
    handleLogout();
    navigate("/home"); // Redirect to home after logout
    setMenuOpen(false);
  };

  return (
    <motion.div
      style={{ opacity }}
      className="absolute top-0 left-0 right-0 z-50 flex justify-between items-center p-8"
    >
      <Link to="/home" className="mix-blend-difference text-white">
        <h1 className="text-3xl font-black tracking-tighter leading-none">
          TIMEWISE.
        </h1>
      </Link>
      <div className="relative">
        <motion.button
          onClick={() => setMenuOpen(!menuOpen)}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="flex items-center gap-2 bg-white/10 backdrop-blur-sm text-white px-4 py-3 rounded-full font-bold text-sm"
        >
          <User size={16} />
          Menu
          <ChevronDown
            size={16}
            className={`transition-transform ${menuOpen ? "rotate-180" : ""}`}
          />
        </motion.button>
        <AnimatePresence>
          {menuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="absolute top-full right-0 mt-2 w-56 bg-black border border-white/20 rounded-xl shadow-2xl overflow-hidden"
            >
              <button
                onClick={goToCalendar}
                className="w-full flex items-center gap-3 text-left px-4 py-3 text-sm text-gray-200 hover:bg-white/10 transition-colors"
              >
                <Calendar size={16} /> My Timeline
              </button>
              <div className="h-px bg-white/10"></div>
              <button
                onClick={onLogout}
                className="w-full flex items-center gap-3 text-left px-4 py-3 text-sm text-red-400 hover:bg-red-500/20 transition-colors"
              >
                <LogOut size={16} /> Logout
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

// --- MAIN DASHBOARD COMPONENT ---
const Dashboard = () => {
  const [selectedCategory, setSelectedCategory] = useState(null);
  const navigate = useNavigate();
  const { scrollY } = useScroll();

  const headerOpacity = useTransform(scrollY, [0, 100], [1, 0]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [selectedCategory]);

  return (
    <div className="relative bg-background min-h-screen text-white font-sans selection:bg-primary-500/30 selection:text-white overflow-x-hidden">
      <DashboardHeader opacity={headerOpacity} />
      {/* Atmosphere */}
      <div className="pointer-events-none absolute inset-0 z-0">
        <div className="absolute -top-40 left-1/2 h-[520px] w-[520px] -translate-x-1/2 rounded-full bg-primary-500/10 blur-[140px]" />
        <div className="absolute top-[35%] right-[-10%] h-[420px] w-[420px] rounded-full bg-secondary-500/15 blur-[130px]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.06),transparent_60%)]" />
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:48px_48px] opacity-40" />
      </div>
      {/* Background Noise Texture */}
      <div
        className="fixed inset-0 pointer-events-none opacity-[0.03] z-[0]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />
      <div className="relative z-10 pt-24 md:pt-32">
        <AnimatePresence mode="wait">
          {!selectedCategory ? (
            <motion.div
              key="main-dashboard"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >

              {/* --- Focus Area --- */}
              <section className="px-6 md:px-12 lg:px-20 py-8">
                <div className="mx-auto max-w-7xl rounded-3xl border border-white/10 bg-gradient-to-br from-primary-500/15 via-white/5 to-secondary-500/15 p-8 md:p-10">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                    <div>
                      <p className="text-xs uppercase tracking-[0.4em] text-primary-300">
                        Today’s focus
                      </p>
                      <h3 className="mt-3 text-2xl md:text-3xl font-bold tracking-tight font-display">
                        Keep your momentum without noise.
                      </h3>
                    </div>
                    <button
                      onClick={() => navigate("/app/calendar")}
                      className="rounded-full bg-white text-black px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-gray-200 transition-colors"
                    >
                      Open Timeline
                    </button>
                  </div>
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                    {focusList.map((item) => (
                      <div
                        key={item.title}
                        className="rounded-2xl border border-white/10 bg-white/5 p-5"
                      >
                        <p className="text-sm font-semibold text-white">{item.title}</p>
                        <p className="mt-2 text-xs text-gray-400 leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              </section>

              {/* --- Core Modules --- */}
              <section className="px-6 md:px-12 lg:px-20 py-16">
                <div className="mx-auto max-w-7xl">
                  <div className="flex flex-col md:flex-row justify-between items-end mb-12">
                    <div>
                      <p className="text-xs uppercase tracking-[0.4em] text-primary-400 mb-3">
                        Workspace
                      </p>
                      <h3 className="text-3xl md:text-5xl font-bold tracking-tight font-display">
                        Jump into your essentials.
                      </h3>
                    </div>
                    <p className="text-gray-400 mt-4 md:mt-0 text-sm max-w-sm text-right">
                      Every module is optimized for speed, so your next action is always one tap away.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {features.map((feature, i) => (
                      <motion.div
                        key={i}
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 + i * 0.1 }}
                        onClick={() => navigate(feature.route)}
                        className="group relative border border-white/10 bg-surface hover:bg-surface-hover p-8 flex flex-col justify-between cursor-pointer overflow-hidden rounded-3xl transition-all duration-500 min-h-[280px]"
                      >
                        <div className="flex justify-between items-start">
                          <div className="w-12 h-12 bg-white/5 group-hover:bg-primary-500/20 text-gray-300 group-hover:text-primary-400 rounded-2xl flex items-center justify-center transition-all duration-300">
                            {feature.icon}
                          </div>
                          <ArrowUpRight className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform group-hover:translate-x-1 group-hover:-translate-y-1 text-white" />
                        </div>

                        <div className="mt-8">
                          <h3 className="text-xl font-bold mb-1 tracking-tight text-white">
                            {feature.title}
                          </h3>
                          <p className="text-[10px] font-semibold uppercase tracking-widest text-primary-400 mb-3 opacity-80">
                            {feature.subtitle}
                          </p>
                          <p className="text-xs text-gray-400 group-hover:text-gray-300 leading-relaxed">
                            {feature.description}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </section>

              {/* --- Explore Services Section --- */}
              <section className="px-6 md:px-12 lg:px-20 py-20 border-t border-white/10">
                <div className="mx-auto max-w-7xl">
                <div className="flex flex-col md:flex-row justify-between items-end mb-16">
                  <h3 className="text-3xl md:text-5xl font-bold tracking-tighter bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-500 font-display">
                    Explore Services.
                  </h3>
                  <p className="text-gray-400 mt-4 md:mt-0 text-sm">
                    Select a category to see available providers.
                  </p>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                  {serviceCategories.map((item, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ y: -5 }}
                      className="group relative aspect-[4/3] overflow-hidden rounded-2xl cursor-pointer border border-white/10"
                      onClick={() => setSelectedCategory(item.label)}
                    >
                      {/* Background Image */}
                      <img 
                        src={item.image} 
                        alt={item.label} 
                        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black opacity-80 group-hover:opacity-60 transition-opacity duration-300" />
                      
                      {/* Content */}
                      <div className="absolute inset-0 flex flex-col items-center justify-center p-4 z-10">
                        <span className="font-bold tracking-widest text-lg uppercase text-white drop-shadow-md border-b-2 border-transparent group-hover:border-primary-500 transition-all pb-1 text-center">
                          {item.label}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
                </div>
              </section>
            </motion.div>
          ) : (
            <motion.div
              key="service-details"
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="min-h-screen pt-32 pb-20 px-6 md:px-12 lg:px-20"
            >
              <div className="mx-auto max-w-7xl">
              <button
                onClick={() => setSelectedCategory(null)}
                className="flex items-center gap-2 text-sm uppercase tracking-widest hover:text-primary-400 mb-8 transition-colors text-gray-400"
              >
                <ChevronLeft size={16} /> Back to Dashboard
              </button>
              <div className="bg-surface border border-white/5 p-4 md:p-8 rounded-3xl min-h-[60vh] backdrop-blur-sm">
                <ServicesByCategory
                  category={selectedCategory}
                  onBack={() => setSelectedCategory(null)}
                  onSelectService={(service) => {
                    const serviceId = service?.service_id || service?.id;
                    navigate(
                      serviceId ? `/app/services/${serviceId}` : "/app/services",
                      { state: { service } }
                    );
                  }}
                />
              </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {!selectedCategory && (
          <section className="px-6 md:px-12 lg:px-20 py-20">
            <div className="mx-auto max-w-7xl rounded-3xl border border-white/10 bg-white/5 p-10 md:p-14 text-center">
              <h3 className="text-3xl md:text-4xl font-bold tracking-tight font-display">
                Ready for what’s next?
              </h3>
              <p className="mt-3 text-gray-400 max-w-2xl mx-auto text-sm md:text-base">
                Jump into your timeline and keep your day flowing without friction.
              </p>
              <div className="mt-6 flex items-center justify-center gap-4 flex-wrap">
                <button
                  onClick={() => navigate("/app/calendar")}
                  className="rounded-full bg-white text-black px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:bg-gray-200 transition-colors"
                >
                  View Calendar
                </button>
                <button
                  onClick={() => navigate("/app/appointments")}
                  className="rounded-full border border-white/30 px-6 py-3 text-xs font-semibold uppercase tracking-widest hover:border-white/60 transition-colors"
                >
                  Manage Appointments
                </button>
              </div>
            </div>
          </section>
        )}

        <AppFooter />
      </div>
    </div>
  );
};

export default Dashboard;
